<template>
  <div
    class="border-solid border-neutral-300 border flex items-center w-full max-w-md p-4 text-gray-500 bg-white rounded-lg shadow-lg z-[1100]"
    role="alert"
  >
    <!-- Icon based on type -->
    <div :class="iconClasses">
      <svg
        v-if="type === 'success'"
        class="w-5 h-5"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"
        />
      </svg>
      <svg
        v-else-if="type === 'error'"
        class="w-5 h-5"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293Z"
        />
      </svg>
      <svg
        v-else-if="type === 'warning'"
        class="w-5 h-5"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM10 15a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm1-4a1 1 0 0 1-2 0V6a1 1 0 0 1 2 0v5Z"
        />
      </svg>
      <svg
        v-else
        class="w-5 h-5"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"
        />
      </svg>
      <!-- Screen reader only text for accessibility -->
      <span class="sr-only">{{ iconLabel }}</span>
    </div>
    <!-- Toast message -->
    <div
      class="flex-grow text-sm font-normal ms-3"
      v-html="sanitizedMessage"
    ></div>
    <!-- Close button -->
    <button
      @click="closeToast"
      type="button"
      class="border-0 ms-auto -mx-1.5 -my-1.5 bg-white text-neutral-400 hover:text-neutral-900 rounded-lg p-1.5 hover:bg-neutral-100 inline-flex items-center justify-center h-8 w-8"
      aria-label="Close"
    >
      <span class="sr-only">Close</span>
      <svg
        class="w-3 h-3"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 14 14"
      >
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
        />
      </svg>
    </button>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";
import DOMPurify from "dompurify";

// Define props
const props = defineProps({
  message: {
    type: String,
    default: "Set yourself free.",
  },
  type: {
    type: String,
    default: "info",
    validator: (value) =>
      ["success", "error", "warning", "info"].includes(value),
  },
});

// Define emits
const emit = defineEmits(["close"]);

// Computed property for icon classes based on type
const iconClasses = computed(() => {
  const baseClasses =
    "inline-flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg";
  switch (props.type) {
    case "success":
      return `${baseClasses} text-green-500 bg-green-100 dark:bg-green-800 dark:text-green-200`;
    case "error":
      return `${baseClasses} text-red-500 bg-red-100 dark:bg-red-800 dark:text-red-200`;
    case "warning":
      return `${baseClasses} text-orange-500 bg-orange-100 dark:bg-orange-700 dark:text-orange-200`;
    default:
      return `${baseClasses} text-blue-500 bg-blue-100 dark:bg-blue-800 dark:text-blue-200`;
  }
});

// Computed property for icon label based on type
const iconLabel = computed(() => {
  switch (props.type) {
    case "success":
      return "Success icon";
    case "error":
      return "Error icon";
    case "warning":
      return "Warning icon";
    default:
      return "Info icon";
  }
});

// Computed property to sanitize the message
const sanitizedMessage = computed(() => {
  return DOMPurify.sanitize(props.message);
});

// Method to close the toast
const closeToast = () => {
  emit("close");
};
</script>